import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { customersApi } from '../../../features/customers/customers.api';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import styles from '../AppPages.module.css';

interface BankAccountFormState {
  bankCode: string;
  accountName: string;
  accountNumber: string;
}

const EMPTY_FORM: BankAccountFormState = {
  bankCode: '',
  accountName: '',
  accountNumber: '',
};

export function BankAccountsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const accounts = useQuery({
    queryKey: ['customer', 'bank-accounts'],
    queryFn: () => customersApi.listBankAccounts(),
  });
  const supportedBanks = useQuery({
    queryKey: ['customer', 'supported-banks'],
    queryFn: () => mandatesApi.listSupportedBanks(),
  });

  const [form, setForm] = useState<BankAccountFormState>(EMPTY_FORM);

  const add = useMutation({
    mutationFn: () =>
      customersApi.addBankAccount({
        bankCode: form.bankCode,
        accountName: form.accountName,
        accountNumber: form.accountNumber,
        isDefault: accounts.data?.length === 0 ? 'true' : 'false',
      }),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      await queryClient.invalidateQueries({ queryKey: ['customer', 'bank-accounts'] });
    },
  });
  const manage = useMutation({
    mutationFn: (input: { id: string; action: 'default' | 'deactivate' }) =>
      input.action === 'default'
        ? customersApi.updateBankAccount(input.id, { isDefault: 'true' })
        : customersApi.deactivateBankAccount(input.id, 'Customer requested deactivation'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer', 'bank-accounts'] });
    },
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Bank accounts</h1>
        <p className={styles.pageSubtitle}>
          Account numbers are encrypted on the server. Only masked values are shown after saving.
        </p>
      </header>

      <section className={styles.card} aria-labelledby="add-account-heading">
        <h2 className={styles.cardTitle} id="add-account-heading">
          Add bank account
        </h2>

        {add.isError && <Alert tone="error">{toDisplayMessage(add.error)}</Alert>}
        {add.isSuccess && <Alert tone="success">Bank account saved.</Alert>}

        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <SelectField
            label="Supported bank"
            name="bankCode"
            value={form.bankCode}
            required
            disabled={supportedBanks.isPending || supportedBanks.isError}
            onChange={(event) =>
              setForm((current) => ({ ...current, bankCode: event.target.value }))
            }
          >
            <option value="">Select a bank</option>
            {(supportedBanks.data ?? [])
              .filter((bank) => bank.status === 'ACTIVE')
              .map((bank) => (
                <option key={bank.bankCode} value={bank.bankCode}>
                  {bank.bankName}
                </option>
              ))}
          </SelectField>
          {supportedBanks.isError && (
            <Alert tone="error">
              Supported banks are temporarily unavailable. Try again later.
            </Alert>
          )}
          <FormField
            label="Account name"
            name="accountName"
            value={form.accountName}
            required
            onChange={(event) => {
              setForm((current) => ({ ...current, accountName: event.target.value }));
            }}
          />
          <FormField
            label="Account number"
            name="accountNumber"
            value={form.accountNumber}
            required
            autoComplete="off"
            inputMode="numeric"
            pattern="[0-9]{10}"
            maxLength={10}
            onChange={(event) => {
              setForm((current) => ({ ...current, accountNumber: event.target.value }));
            }}
          />
          <Button type="submit" disabled={add.isPending}>
            Save account
          </Button>
        </form>
      </section>

      <section className={styles.card} aria-labelledby="accounts-heading">
        <h2 className={styles.cardTitle} id="accounts-heading">
          Saved accounts
        </h2>

        {accounts.isPending && <PageSpinner label="Loading bank accounts" />}
        {accounts.isError && <Alert tone="error">{toDisplayMessage(accounts.error)}</Alert>}

        {accounts.isSuccess && accounts.data.length === 0 && (
          <p className={styles.empty}>No bank accounts registered.</p>
        )}

        {accounts.isSuccess && accounts.data.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Bank</th>
                  <th scope="col">Account name</th>
                  <th scope="col">Account number</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.data.map((account) => (
                  <tr key={account.id}>
                    <td>{account.bankNameSnapshot}</td>
                    <td>{account.accountName}</td>
                    <td>{account.accountNumberMasked}</td>
                    <td>
                      <StatusBadge status={account.verificationStatus} />
                      {account.isDefault === 'true' ? ' · Default' : ''}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {account.isDefault !== 'true' && (
                          <Button
                            variant="secondary"
                            disabled={manage.isPending}
                            onClick={() => manage.mutate({ id: account.id, action: 'default' })}
                          >
                            Make default
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          disabled={manage.isPending}
                          onClick={() => manage.mutate({ id: account.id, action: 'deactivate' })}
                        >
                          Deactivate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
